import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Bouton favori générique.
 * type ∈ { "offer" | "business" | "category" | "city" }
 * id = offer_id | business_id | category_slug | city_name
 */
export default function FavoriteButton({ type = "offer", id, size = "md", className = "", onToggle }) {
  const { user } = useAuth();
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);

  // Hydrate l'état initial à partir du serveur si l'utilisateur est connecté
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!user || !id) return;
      try {
        const { data } = await api.get("/me/favorites/all");
        if (!alive) return;
        if (type === "offer") setActive((data.offers || []).some((o) => o.id === id));
        if (type === "business") setActive((data.businesses || []).some((b) => b.id === id));
        if (type === "category") setActive((data.categories || []).some((c) => c.slug === id));
        if (type === "city") setActive((data.cities || []).some((c) => c.name === id));
      } catch { /* ignore */ }
    };
    run();
    return () => { alive = false; };
  }, [user, type, id]);

  const toggle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!user) { toast.info("Connectez-vous pour enregistrer vos favoris."); return; }
    if (loading || !id) return;
    setLoading(true);
    const endpoint = type === "offer" ? `/me/favorites/${id}` : `/me/favorites/${type}/${encodeURIComponent(id)}`;
    try {
      if (active) {
        await api.delete(endpoint);
        setActive(false);
        toast.success("Retiré des favoris");
      } else {
        await api.post(endpoint);
        setActive(true);
        toast.success("Ajouté aux favoris");
      }
      onToggle?.(!active);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible pour l'instant");
    } finally { setLoading(false); }
  };

  const sz = size === "sm" ? 14 : size === "lg" ? 20 : 16;
  const btn = size === "sm" ? "w-8 h-8" : size === "lg" ? "w-11 h-11" : "w-9 h-9";
  return (
    <button onClick={toggle} disabled={loading}
            data-testid={`fav-${type}-btn-${id}`}
            aria-label={active ? "Retirer des favoris" : "Ajouter aux favoris"}
            className={`${btn} grid place-items-center rounded-full bg-white/95 hover:bg-white shadow-sm border border-slate-200 disabled:opacity-50 ${className}`}>
      <Heart size={sz} className={active ? "fill-rose-500 text-rose-500" : "text-slate-600"}/>
    </button>
  );
}
